import {
  SystemNodeHexagon,
  getTruncateMaxLinesStyle,
} from '@agilelab/plugin-wb-platform';
import { Box, Theme, Tooltip, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { Handle, NodeProps, Position } from 'reactflow';
import { usePrototypeInformationGraphContext } from '../../../PrototypeInformationGraphContext';

type PrototypeNodeData = {};

export type PrototypeNodeProps = NodeProps<PrototypeNodeData>;

export const PROTOTYPE_NODE_ID = 'protoype';

export const HEXAGON_SIZE = 210;

const useStyles = makeStyles<
  Theme,
  { connectedLeft: boolean; connectedRight: boolean }
>(
  theme => ({
    root: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: theme.spacing(1),
      alignItems: 'center',
      width: HEXAGON_SIZE,
      height: HEXAGON_SIZE,
      cursor: 'default',
    },
    handle: {
      width: '12px',
      height: '12px',
      border: 'none',
      cursor: 'default',
      top: 'calc(50% - 4px)',
    },
    handleRight: {
      visibility: props => (props.connectedRight ? 'visible' : 'hidden'),
      backgroundColor: theme.palette.accentSecondary.main,
    },
    handleLeft: {
      visibility: props => (props.connectedLeft ? 'visible' : 'hidden'),
      backgroundColor: theme.palette.accent.dark,
    },
    name: {
      color: theme.palette.secondary.dark,
      fontWeight: 500,
      textAlign: 'center',
      ...getTruncateMaxLinesStyle(2, false),
      maxWidth: '125px',
    },
    domain: {
      ...getTruncateMaxLinesStyle(),
      maxWidth: '100px',
    },
    content: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: theme.spacing(1),
      alignItems: 'center',
      width: '100%',
      height: '100%',
    },
    actions: {
      position: 'absolute',
      bottom: '15%',
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(1),
      alignItems: 'center',
    },
  }),
  { name: 'PrototypeNode' },
);

export const PrototypeNode = ({}: PrototypeNodeProps) => {
  const { prototype } = usePrototypeInformationGraphContext();

  const classes = useStyles({
    connectedLeft: !!(prototype.dataSources ?? []).length,
    connectedRight: !!(prototype.businessConcepts ?? []).length,
  });

  const prototypeNameLabel = prototype.name ?? 'New Data Product';
  const prototypeDomainLabel = prototype.domain ?? 'Pick a domain';

  return (
    <>
      <div className={classes.root}>
        <SystemNodeHexagon />
        <Box className={classes.content}>
          <Tooltip title={prototypeNameLabel}>
            <Typography className={classes.name} variant="h6">
              {prototypeNameLabel}
            </Typography>
          </Tooltip>
          <Tooltip title={prototypeDomainLabel}>
            <Typography className={classes.domain} variant="body2">
              {prototypeDomainLabel}
            </Typography>
          </Tooltip>
        </Box>
        <Handle
          type="source"
          id="right"
          className={clsx(classes.handle, classes.handleRight)}
          style={{ right: -2, cursor: 'default' }}
          position={Position.Right}
        />
        <Handle
          type="source"
          id="left"
          className={clsx(classes.handle, classes.handleLeft)}
          style={{ left: -2, cursor: 'default' }}
          position={Position.Left}
        />
      </div>
    </>
  );
};
